import * as Sentry from "@sentry/react";
import createFetchClient, { Middleware } from "openapi-fetch";
import createQueryClient from "openapi-react-query";

import type { paths } from "../generated/forms-api";

const problemReason = async (response: Response): Promise<string | undefined> => {
    try {
        const body: unknown = await response.clone().json();
        if (body && typeof body === "object" && "reason" in body && typeof body.reason === "string") return body.reason;
    } catch {
        return undefined;
    }
    return undefined;
};

export const formsSentryMiddleware: Middleware = {
    async onResponse({ request, response, schemaPath }) {
        if (response.ok || response.status === 401 || response.status === 409) return;
        if (response.status === 404 && request.method === "GET" && schemaPath === "/api/supporting-documents") return;
        const reason = await problemReason(response);
        Sentry.captureException(
            new Error(
                `[forms] ${request.method} ${schemaPath} failed with status ${response.status}${reason ? `: ${reason}` : ""}`
            ),
            {
                tags: {
                    formsOperation: schemaPath,
                    method: request.method,
                    statusCode: response.status,
                    kind: "http",
                    reason: reason?.slice(0, 200),
                },
                fingerprint: ["forms", request.method, schemaPath, String(response.status)],
                extra: { url: request.url, reason },
            }
        );
    },
    onError({ request, schemaPath, error }) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        Sentry.captureException(error, {
            tags: { formsOperation: schemaPath, method: request.method, kind: "network" },
            extra: { url: request.url },
        });
    },
};

export const formsFetch = createFetchClient<paths>({ baseUrl: process.env.REACT_APP_FORMS_API_URI });
formsFetch.use(formsSentryMiddleware);

export const formsApi = createQueryClient(formsFetch);

export const authHeader = (token?: string) => ({ Authorization: `Bearer ${token}` });

export class FormsApiError extends Error {
    constructor(
        operation: string,
        readonly status: number
    ) {
        super(`${operation} failed with status ${status}`);
    }
}

export const isNotFound = (error: unknown): boolean => error instanceof FormsApiError && error.status === 404;

export const isClosed = (error: unknown): boolean => error instanceof FormsApiError && error.status === 409;

export const retryServerErrors = (failureCount: number, error: unknown): boolean =>
    !(error instanceof FormsApiError && error.status < 500) && failureCount < 3;

export const unwrap = <T>(result: { data?: T; response: Response }, operation: string): T => {
    if (!result.data) {
        throw new FormsApiError(operation, result.response.status);
    }
    return result.data;
};

type AnswerBody = { id?: string; value?: string[]; fileNames?: string[]; comment?: string };

export const answerBody = (body: AnswerBody): AnswerBody =>
    body.value === undefined && body.fileNames === undefined && body.comment === undefined
        ? { ...body, value: [] }
        : body;

export const saveAnswer = async (
    token: string | undefined,
    assessmentId: string,
    questionId: string,
    body: AnswerBody
) =>
    unwrap(
        await formsFetch.PATCH("/api/ast/{id}/answers/{questionId}", {
            params: { path: { id: assessmentId, questionId } },
            headers: authHeader(token),
            body: answerBody(body),
        }),
        "save answer"
    );

export const saveAnswerV2 = async (
    token: string | undefined,
    assessmentId: string,
    questionKey: string,
    body: NonNullable<
        paths["/api/ast/{id}/answers-v2/{questionKey}"]["put"]["requestBody"]
    >["content"]["application/json"],
    keepalive = false
) =>
    unwrap(
        await formsFetch.PUT("/api/ast/{id}/answers-v2/{questionKey}", {
            params: { path: { id: assessmentId, questionKey } },
            headers: authHeader(token),
            body,
            keepalive,
        }),
        "save answer v2"
    );
